import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddOn, Service } from '../types';
import { getAddOns, getService } from '../services/supabase';
import './AddOnSelection.css';

interface NavigationMeta {
  service?: Service | null;
  travelFee?: number;
  travelMiles?: number;
  addOns?: AddOn[];
}

const AddOnSelection: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navState = (location.state as any) || {};
  const booking = navState.booking || (() => {
    try {
      const raw = sessionStorage.getItem('pendingBooking');
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return null;
  })();
  const initialMeta: NavigationMeta = navState.meta || {};

  const [service, setService] = useState<Service | null>(initialMeta.service ?? null);
  const [addOns, setAddOns] = useState<AddOn[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>(
    (initialMeta.addOns || []).map(addOn => addOn.id)
  );
  const [loading, setLoading] = useState(true);
  const [redirected, setRedirected] = useState(false);
  const travelFee = initialMeta.travelFee ?? booking?.travel_fee ?? 0;
  const travelMiles = initialMeta.travelMiles ?? booking?.travel_miles ?? 0;

  useEffect(() => {
    if (!booking) {
      navigate('/book');
    }
  }, [booking, navigate]);

  useEffect(() => {
    const resolveService = async () => {
      if (!booking || service) return;
      try {
        const fetched = await getService(booking.service_id);
        setService(fetched);
      } catch (error) {
        console.error('Failed to fetch service for add-on selection:', error);
      }
    };
    resolveService();
  }, [booking, service]);

  useEffect(() => {
    const loadAddOns = async () => {
      if (!booking) return;
      try {
        const allAddOns = await getAddOns();
        const filtered = allAddOns.filter(addOn => addOn.service_id === booking.service_id);
        setAddOns(filtered);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load add-ons:', error);
        setAddOns([]);
        setLoading(false);
      }
    };
    loadAddOns();
  }, [booking]);

  useEffect(() => {
    if (!loading && addOns.length === 0 && booking && service && !redirected) {
      setRedirected(true);
      navigate('/payment', {
        state: {
          booking,
          meta: {
            service,
            travelFee,
            travelMiles,
            addOns: []
          }
        }
      });
    }
  }, [addOns.length, booking, service, loading, navigate, travelFee, travelMiles, redirected]);

  const selectedAddOns = useMemo(
    () => addOns.filter(addOn => selectedIds.includes(addOn.id)),
    [addOns, selectedIds]
  );

  const addOnTotal = useMemo(
    () => selectedAddOns.reduce((sum, addOn) => sum + addOn.price, 0),
    [selectedAddOns]
  );

  const basePrice = service?.price ?? booking?.total_amount ?? 0;
  const total = basePrice + addOnTotal + travelFee;

  const toggleAddOn = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(existing => existing !== id) : [...prev, id]
    );
  };

  const handleContinue = () => {
    if (!booking || !service) return;
    navigate('/payment', {
      state: {
        booking,
        meta: {
          service,
          travelFee,
          travelMiles,
          addOns: selectedAddOns
        }
      }
    });
  };

  if (!booking) {
    return null;
  }

  if (loading) {
    return (
      <div className="addon-selection loading">
        <div className="loading-spinner"></div>
        <p>Loading available add-ons...</p>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="addon-selection">
        <p>Unable to load service details. Please try again.</p>
        <button className="addon-btn" onClick={() => navigate('/book')}>
          Return to booking
        </button>
      </div>
    );
  }

  return (
    <div className="addon-selection">
      <div className="addon-container">
        <header className="addon-header">
          <h1>Select Add-ons</h1>
          <p>
            Enhance your <strong>{service.name}</strong> service by choosing any of the
            optional add-ons below. You can continue without selecting any add-ons.
          </p>
        </header>

        <div className="addon-content">
          <section className="addon-list">
            {addOns.map(addOn => {
              const checked = selectedIds.includes(addOn.id);
              return (
                <label key={addOn.id} className={`addon-card ${checked ? 'selected' : ''}`}>
                  <div className="addon-card-header">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAddOn(addOn.id)}
                    />
                    <div>
                      <h3>{addOn.name}</h3>
                      <span className="addon-price">${addOn.price.toFixed(2)}</span>
                    </div>
                  </div>
                  <p className="addon-description">{addOn.description}</p>
                </label>
              );
            })}
            {addOns.length === 0 && (
              <p>No add-ons are available for this service.</p>
            )}
          </section>

          <aside className="addon-summary">
            <h2>Summary</h2>
            <div className="summary-row">
              <span>Service</span>
              <span>${basePrice.toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>Add-ons</span>
              <span>${addOnTotal.toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>Travel Fee</span>
              <span>${travelFee.toFixed(2)}</span>
            </div>
            <div className="summary-total">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
            <button className="addon-btn" onClick={handleContinue}>
              Continue to Payment
            </button>
            <button
              className="addon-btn secondary"
              onClick={() => navigate('/payment', {
                state: {
                  booking,
                  meta: {
                    service,
                    travelFee,
                    travelMiles,
                    addOns: []
                  }
                }
              })}
            >
              Skip add-ons
            </button>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default AddOnSelection;
